import { Component, OnDestroy, AfterViewInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ActivityTypeEnum,
  ActivityVisibilityEnum,
  SortEnum,
  TimeslotStatusEnum,
} from '@sport-activities/sdk';

import { AbstractActivityPages } from '~/attendee/components/pages/activity-detail/classes/abstract-activity-pages';
import { Logger } from '~/core/logger.service';
import { Activity } from '~/core/model/activity.model';
import { CustomHeadTagService } from '~/core/services/custom-head-tag/custom-head-tag.service';
import { MicrodataTypeEnum } from '~/core/services/custom-head-tag/microdata.enum';
import { OpenGraphService } from '~/core/services/open-graph/open-graph.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { RouterHistoryService } from '~/core/services/router-history/router-history.service';
import { SportsService } from '~/core/services/sports/sports.service';
import { TitleService } from '~/core/services/title/title.service';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ActivityService } from '~/core/services/activity/activity.service';

@Component({
  selector: 'app-attendee-activity-detail',
  templateUrl: './activity-detail.component.html',
  styleUrls: ['./activity-detail.component.scss'],
})
export class AttendeeActivityDetailComponent
  extends AbstractActivityPages
  implements OnDestroy, AfterViewInit
{
  constructor(
    public readonly activatedRoute: ActivatedRoute,
    public readonly titleService: TitleService,
    public readonly routerHistory: RouterHistoryService,
    public readonly openGraphService: OpenGraphService,
    public readonly sportsService: SportsService,
    public readonly redirectionService: RedirectionService,
    public readonly toggleService: ToggleService,
    public readonly customHeadTag: CustomHeadTagService,
    public readonly logger: Logger,
    protected readonly organizerSharedActivityService: OrganizerSharedActivityService,
    public readonly activityService: ActivityService
  ) {
    super(
      activatedRoute,
      titleService,
      routerHistory,
      openGraphService,
      sportsService,
      redirectionService,
      toggleService,
      customHeadTag,
      organizerSharedActivityService,
      logger
    );
  }

  public ngOnDestroy(): void {
    this.customHeadTag.destroyMicrodata();
  }

  public ngAfterViewInit(): void {
    // Call activityService to display timelsot only when all timeslots are full
    if (!this.timeslots?.length) {
      this.activityService
        .getActivityTimeslots(
          this.activity?.sku?.toString(),
          {
            excludeFull: false,
            timeslotStatus: 'PUBLISHED' as TimeslotStatusEnum,
            sort: {
              order: 'asc' as SortEnum,
              by: 'startDate',
            },
            startDate: new Date().toISOString(),
          },
          1,
          4
        )
        .subscribe((timeslots) => {
          this.timeslots = timeslots;
        });
    }
  }

  public constructMicrodata(): void {
    const activityOffers =
      this.activity?.indicativeOffers?.length > 1
        ? {
            '@type': 'AggregateOffer',
            lowPrice: this.activity?.minPriceOffer?.price,
            highPrice: this.activity?.maxPriceOffer?.price,
            offerCount: this.activity.indicativeOffers?.length,
            priceCurrency: this.activity?.startingPrice?.currency,
            validFrom: this.timeslots
              ? this.timeslots[this.timeslots?.length - 1]?.endDate
              : null,
          }
        : {
            '@type': 'Offer',
            price: this.activity?.startingPrice?.amount,
            priceCurrency: this.activity?.startingPrice?.currency,
            validFrom: this.timeslots
              ? this.timeslots[this.timeslots?.length - 1]?.endDate
              : null,
          };

    const activityAddress = this.activity?.isLive
      ? {
          '@type': 'VirtualLocation',
          url: this.activity?.liveUrl,
        }
      : {
          '@type': 'Place',
          name: `${this.activity?.location?.address?.streetAddress},
            ${this.activity?.location?.address?.postalCode} ${this.activity?.location?.address?.addressLocality}`,
          address: {
            '@type': 'PostalAddress',
            addressLocality: this.activity?.location?.address?.addressLocality,
            addressRegion: this.activity?.location?.address?.addressRegion,
            postalCode: this.activity?.location?.address?.postalCode,
            streetAddress: this.activity?.location?.address?.streetAddress,
          },
        };

    const data = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: this.activity?.name,
      description: this.activity?.description,
      startDate: this.timeslots?.length ? this.timeslots[0]?.startDate : null,
      endDate: this.timeslots?.length
        ? this.timeslots[this.timeslots.length - 1]?.endDate
        : null,
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: `https://schema.org/${
        this.activity?.isLive
          ? 'OnlineEventAttendanceMode'
          : 'OfflineEventAttendanceMode'
      }`,
      image: this.activity?.image?.url,
      offers: activityOffers,
      location: activityAddress,
    };

    if (this.timeslots?.length) {
      this.customHeadTag.setMicrodata(data, MicrodataTypeEnum.Event);
    }
  }

  public handleRedirections(
    activityDetailData: { [key: string]: Activity },
    path: string
  ): void {
    if (
      activityDetailData?.activity?.type === ('LIVE' as ActivityTypeEnum) &&
      path.includes('attendee.show-activity')
    ) {
      const isPreviewParam = activityDetailData?.isPreview
        ? { queryParams: { preview: activityDetailData?.isPreview } }
        : {};
      this.redirectionService.redirect(
        `/attendee.show-live-activity/${activityDetailData?.activity?.sku}`,
        isPreviewParam
      );
    }
  }

  public setContentMeta(): void {
    if (this.activity?.visibility === ('PRIVATE' as ActivityVisibilityEnum)) {
      this.customHeadTag.addTag('meta', {
        name: 'robots',
        content: 'noindex, nofollow',
      });
    }
  }

  public setTitle(): void {
    this.titleService.setPageTitle('seo.attendee.activity_detail.title', {
      title: this.activity?.name,
    });
  }
}
